import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import styles from './App.module.css';
import './styles/globals.css';
import { useFCM } from './firebase/useFCM';
import { BusinessSettingsProvider } from './context/BusinessSettingsContext';

import Layout from './Component/Layout/Layout';
import FloatingSupportButton from './Component/Support/FloatingSupportButton';
import OrderReport from './Component/OrderReport/OrderReport';
import CustomerAnalytics from './Component/CustomerAnalytics/CustomerAnalytics';
import MetaAnalytics from './Component/MetaAnalytics/MetaAnalytics';
import ClaudeMcp from './Component/ClaudeMcp/ClaudeMcp';
import ChatAutomation from './Component/ChatAutomation/ChatAutomation';
import Returns from './Component/Returns/Returns';
import AICalling from './Component/AICalling/AICalling';
import Accounts from './Component/Accounts/Accounts';
import Purchase from './Component/Purchase/Purchase';
import Expense from './Component/Expense/Expense';
import BusinessEmail from './Component/BusinessEmail/BusinessEmail';
import BuyDomain from './Component/BuyDomain/BuyDomain';
import NewStore from './Component/NewStore/NewStore';
import DnsRecord from './Component/DnsRecord/DnsRecord';
import FB from './Component/ApiAccess/Facebook/FacebookAuth';
import Forgot from './Component/LoginSignup/Forgot';
import ForgotVerify from './Component/LoginSignup/ForgotVerify';
import LoginSignup from './Component/LoginSignup/LoginSignup';
import LandingHome from './Component/LandingHome/LandingHome';

import SetStoreName from './Component/SetStoreName/SetStoreName';
import GetStarted from './Component/GetStarted/GetStarted';
import Dashboard from './Component/Dashboard/Dashboard';
import Addproduct from './Component/AddProduct/Addproduct';
import Product from './Component/Product/Product';
import Order from './Component/Order/Order';
import Theme from './Component/Theme/Theme';
import TemplateManager from './Component/Template/Template';
import TemplateEditorPage from './Component/Template/TemplateEditorPage';
import OrderList from './Component/OrderList/OrderList';
import TeamLayout from './Component/TeamLayout/TeamLayout';
import { SoftphoneGate } from './Component/Softphone/Softphone';
import EditProduct from './Component/EditProduct/EditProduct';
import Cart from './Component/Cart/Cart';
import CheckOut from './Component/CheckOut/CheckOut';
import EditOrder from './Component/EditOrder/EditOrder';
import VisitorVideo from './Component/VisitorVideo/VisitorVideo';
import Category from './Component/CategorySubCategory/Category';
// SubCategoryList removed — categories are now managed in a unified tree view at /category
import BrandPage from './Component/Brand/Brand';
import CategorySelect from './Component/CategorySelect/CategorySelect';
import Store from './Component/Store/Store';
import Customer from './Component/Customer/Customer';
import Team from './Component/Team/Team';
import ProductEdit from './Component/ProductEdit/ProductEdit';
import TeamLogin from './Component/TeamLogin/Login';
import Billing from './Component/Billing/Billing';
import LandingPage from './Component/LandingPage/LandingPage';
import LandingList from './Component/LandingList/LandingList';

// Bkash
import Success from './Component/Bkash/Success';
import Fail from './Component/Bkash/Fail';
import Callback from './Component/Bkash/Callback';
import CallbackSMS from './Component/Bkash/CallbackSMS';
import Profile from './Component/Profile/Profile';
import Settings from './Component/Settings/Settings';
import BusinessSettings from './Component/BusinessSettings/BusinessSettings';
import Policy from './Component/Policy/Policy';
import BannerList from './Component/BannerList/BannerList';
import AddBanner from './Component/AddBanner/AddBanner';
import EditBanner from './Component/EditBanner/EditBanner';
import Landing1Home from './Component/Landing1/components/Home';
import Landing2Home from './Component/Landing/components/Home';
import EditLanding2Home from './Component/EditLanding/components/Home';
import UpdateLanding2Home from './Component/UpdateLanding/components/Home';
import EditLanding1Home from './Component/EditLanding1/components/Home';
import UpdateLanding1Home from './Component/UpdateLanding1/components/Home';

// Blog Management Components
import BlogList from './Component/Blog/BlogList';
import AddBlog from './Component/Blog/AddBlog';
import EditBlog from './Component/Blog/EditBlog';

// SEO Management Components
import FAQManagement from './Component/SEO/FAQManagement';
import ReviewManagement from './Component/SEO/ReviewManagement';
import SEOSettings from './Component/SEO/SEOSettings';
import HomePageSEO from './Component/SEO/HomePageSEO';

// User Email Management Components
import UserEmailManagement from './Component/UserEmailManagement/UserEmailManagement';
import UserEmailInbox from './Component/UserEmailInbox/UserEmailInbox';
import MailInbox from './Component/MailInbox/Inbox';

// Contact Messages
import ContactMessages from './Component/ContactMessages/ContactMessages';

// Super Admin Components
import {
    SupperAdminRoute,
    Dashboard as SuperAdminDashboard,
    Stores as SuperAdminStores,
    Users as SuperAdminUsers,
    UserSubscriptionInfo as SuperAdminUserSubscriptionInfo,
    Packages as SuperAdminPackages,
    Billing as SuperAdminBilling,
    Analytics as SuperAdminAnalytics,
    Settings as SuperAdminSettings,
    DNSConfig as SuperAdminDNSConfig,
    DNSRecords as SuperAdminDNSRecords,
    Domains as SuperAdminDomains,
    Logs as SuperAdminLogs,
    SMSMarketing as SuperAdminSMSMarketing,
    ManualSubscription as SuperAdminManualSubscription,
    SupportTickets as SuperAdminSupportTickets,
    EmailManagement as SuperAdminEmailManagement,
    EmailInbox as SuperAdminEmailInbox,
    CallNumbers as SuperAdminCallNumbers
} from './Component/SupperAdmin';

const App = () => {
    // Initialize Firebase Cloud Messaging
    const { fcmToken, notification, isPermissionGranted } = useFCM();

    useEffect(() => {
        if (fcmToken) {
            console.log('App initialized with FCM token:', fcmToken);
        }
    }, [fcmToken]);

    function PrivateRoute({ children }) {
        const token = localStorage.getItem('token');
        if (!token) {
            return <Navigate to="/login" replace />;
        }
        // A team member's login keeps the seller id in "id" (the order pages
        // need it) — keep them on the team pages, not the seller's admin.
        if (localStorage.getItem('teamid')) {
            return <Navigate to="/team-order" replace />;
        }
        return children;
    }

    // Paid-only gate (free trial removed). Unpaid users are sent to the launch
    // offer page before they can create a store. The cached 'IsPaid' flag is
    // written by GetStarted/HeaderMain/Callback from /get-subscription; if it is
    // missing we route through /get-started, which re-checks and forwards a
    // genuinely-paid user onward. Super admins (role 1) bypass the gate.
    function PaidRoute({ children }) {
        const token = localStorage.getItem('token');
        if (!token) {
            return <Navigate to="/login" replace />;
        }
        if (localStorage.getItem('teamid')) {
            return <Navigate to="/team-order" replace />;
        }
        const isSuper = localStorage.getItem('role') === '1';
        const isPaid = localStorage.getItem('IsPaid') === 'true';
        if (!isSuper && !isPaid) {
            const offerActive = localStorage.getItem('offerActive') === 'true';
            return <Navigate to={offerActive ? '/get-started' : '/billing'} replace />;
        }
        return children;
    }

    function PrivateRouteTeam({ children }) {
        const token = localStorage.getItem('teamid');
        if (!token) {
            return <Navigate to="/teamlogin" replace />;
        }
        return children;
    }

    function IsLogin({ children }) {
        const token = localStorage.getItem('token');
        const createStore = localStorage.getItem('CreateStore');
        const userId = localStorage.getItem('id');

        // A team member's session: still show the login page (a seller login clears it).
        if (token && !localStorage.getItem('teamid')) {
            const isSuper = localStorage.getItem('role') === '1';
            const isPaid = localStorage.getItem('IsPaid') === 'true';
            // Unpaid: active offer → /get-started, expired → /billing.
            if (!isSuper && !isPaid) {
                const offerActive = localStorage.getItem('offerActive') === 'true';
                return <Navigate to={offerActive ? '/get-started' : '/billing'} replace />;
            }
            // Check if user needs to set up store
            if (createStore === 'false' || createStore === false || createStore === 'False' || createStore === 'FALSE') {
                console.log('IsLogin - Redirecting to store setup:', userId);
                return <Navigate to={`/set-store-name/${userId}`} replace />;
            }
            console.log('IsLogin - Redirecting to dashboard');
            return <Navigate to="/dashboard" replace />;
        } else {
            return children;
        }
    }

    /**
     * AuthGatedSupport — only renders the floating support button for
     * authenticated users. The landing/login/signup pages have their
     * own contact widgets and shouldn't show this one.
     */
    function AuthGatedSupport() {
        const token = localStorage.getItem('token');
        if (!token) return null;
        return <FloatingSupportButton />;
    }

    /**
     * Root route handler:
     *  - /                    → marketing landing page
     *  - /?tab=login          → login form
     *  - /?tab=signup         → signup form
     * Authed users always bounce to dashboard via IsLogin.
     */
    function RootRoute() {
        const location = useLocation();
        const tab = new URLSearchParams(location.search).get('tab');
        if (tab === 'login' || tab === 'signup') {
            return (
                <IsLogin>
                    <LoginSignup />
                </IsLogin>
            );
        }
        return (
            <IsLogin>
                <LandingHome />
            </IsLogin>
        );
    }

    return (
        <div className={styles.appContainer}>
            <BusinessSettingsProvider>
                <BrowserRouter>
                    {/* Floating Support Button — only for authenticated users */}
                    <AuthGatedSupport />

                    {/* AI calling softphone — one instance for the whole app (calls survive page changes) */}
                    <SoftphoneGate />

                    {/* {state.length && <ManualOrder />}
        {state.length && <AddtoCart />} */}
                    <Routes>
                    <Route path="/" element={<RootRoute />} />
                    <Route
                        path="/get-started"
                        element={
                            <PrivateRoute>
                                <GetStarted />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/set-store-name/:id"
                        element={
                            <PaidRoute>
                                <SetStoreName />
                            </PaidRoute>
                        }
                    />
                    <Route path="/store" element={<Store />} />
                    <Route
                        path="/login"
                        element={
                            <IsLogin>
                                <LoginSignup />
                            </IsLogin>
                        }
                    />
                    <Route
                        path="/forgot-password"
                        element={
                            <IsLogin>
                                <Forgot />
                            </IsLogin>
                        }
                    />
                    <Route
                        path="/forgot-verify"
                        element={
                            <IsLogin>
                                <ForgotVerify />
                            </IsLogin>
                        }
                    />
                    <Route
                        path="/teamlogin"
                        element={
                            <IsLogin>
                                <TeamLogin />
                            </IsLogin>
                        }
                    />
                    <Route
                        path="/signup"
                        element={
                            <IsLogin>
                                <LoginSignup />
                            </IsLogin>
                        }
                    />
                    <Route
                        path="/dashboard"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <Dashboard />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/expense"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <Expense />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/business-email"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <BusinessEmail />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/email-management"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <UserEmailManagement />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/email-inbox"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <MailInbox />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    {/* the previous inbox, kept for a while as a fallback (2026-11-04) */}
                    <Route
                        path="/email-inbox-classic"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <UserEmailInbox />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/buy-domain"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <BuyDomain />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/accounts"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <Accounts />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/purchase"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <Purchase />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/team-order"
                        element={
                            <PrivateRouteTeam>
                                <TeamLayout>
                                    <OrderList team />
                                </TeamLayout>
                            </PrivateRouteTeam>
                        }
                    />
                    <Route
                        path="/team-returns"
                        element={
                            <PrivateRouteTeam>
                                <TeamLayout>
                                    <Returns />
                                </TeamLayout>
                            </PrivateRouteTeam>
                        }
                    />
                    <Route
                        path="/edit-team-order"
                        element={
                            <PrivateRouteTeam>
                                <TeamLayout>
                                    <EditOrder />
                                </TeamLayout>
                            </PrivateRouteTeam>
                        }
                    />
                    <Route
                        path="/team"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <Team />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/add-product"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <Addproduct />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/add-product/:id"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <Addproduct />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/product"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <Product />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/create-order"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <Order />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/order-list"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <OrderList />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/dns-list"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <DnsRecord />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/edit-product"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <EditProduct />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/cart"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <Cart />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/checkout"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <CheckOut />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/edit-order"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <EditOrder />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/visitor-video"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <VisitorVideo />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/special-category"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <CategorySelect />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/subcategories/:categoryId"
                        element={<Navigate to="/category" replace />}
                    />
                    <Route
                        path="/category"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <Category />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/brand"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <BrandPage />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/advance-report"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <OrderReport />
                                </Layout>
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path="/customer-analytics"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <CustomerAnalytics />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/meta-analytics"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <MetaAnalytics />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/returns"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <Returns />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/chat-automation"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <ChatAutomation />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/claude-mcp"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <ClaudeMcp />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/ai-calling"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <AICalling />
                                </Layout>
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path="/new-store"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <NewStore />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/product-edit"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <ProductEdit />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/billing"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <Billing />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/contact-messages"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <ContactMessages />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/customers"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <Customer />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/profile"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <Profile />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/settings"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <Settings />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/business-settings"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <BusinessSettings />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/policy"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <Policy />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    {/* Blog Routes */}
                    <Route
                        path="/blog-list"
                        element={
                            <PrivateRoute>
                                <BlogList />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/blog/add"
                        element={
                            <PrivateRoute>
                                <AddBlog />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/blog/edit/:id"
                        element={
                            <PrivateRoute>
                                <EditBlog />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path="/banner-list"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <BannerList />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/add-banner"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <AddBanner />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/edit-banner"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <EditBanner />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/landing-page"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <LandingPage />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/landing-list"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <LandingList />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/landing/:id"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <Landing1Home />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/landing1/:id"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <Landing2Home />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/landing1/edit/:id"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <EditLanding2Home />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/landing1/update/:id"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <UpdateLanding2Home />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/landing/edit/:id"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <EditLanding1Home />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/landing/update/:id"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <UpdateLanding1Home />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/theme"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <Theme />
                                </Layout>
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path="/template"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <TemplateManager />
                                </Layout>
                            </PrivateRoute>
                        }
                    />

                    {/* Full-screen visual editor — no Layout wrapper */}
                    <Route
                        path="/template/edit/:templateName"
                        element={
                            <PrivateRoute>
                                <TemplateEditorPage />
                            </PrivateRoute>
                        }
                    />

                    {/* SEO Management Routes */}
                    <Route
                        path="/seo/faq"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <FAQManagement />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/seo/reviews"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <ReviewManagement />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/seo/settings"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <SEOSettings />
                                </Layout>
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/seo/home-seo"
                        element={
                            <PrivateRoute>
                                <Layout>
                                    <HomePageSEO />
                                </Layout>
                            </PrivateRoute>
                        }
                    />

                    {/* Super Admin Routes - Protected by role = 1 */}
                    <Route
                        path="/super-admin-dashboard"
                        element={
                            <SupperAdminRoute>
                                <SuperAdminDashboard />
                            </SupperAdminRoute>
                        }
                    />
                    <Route
                        path="/superadmin"
                        element={
                            <SupperAdminRoute>
                                <SuperAdminDashboard />
                            </SupperAdminRoute>
                        }
                    />
                    <Route
                        path="/superadmin/stores"
                        element={
                            <SupperAdminRoute>
                                <SuperAdminStores />
                            </SupperAdminRoute>
                        }
                    />
                    <Route
                        path="/superadmin/users"
                        element={
                            <SupperAdminRoute>
                                <SuperAdminUsers />
                            </SupperAdminRoute>
                        }
                    />
                    <Route
                        path="/superadmin/user-subscription-info"
                        element={
                            <SupperAdminRoute>
                                <SuperAdminUserSubscriptionInfo />
                            </SupperAdminRoute>
                        }
                    />
                    <Route
                        path="/superadmin/manual-subscription"
                        element={
                            <SupperAdminRoute>
                                <SuperAdminManualSubscription />
                            </SupperAdminRoute>
                        }
                    />
                    <Route
                        path="/superadmin/packages"
                        element={
                            <SupperAdminRoute>
                                <SuperAdminPackages />
                            </SupperAdminRoute>
                        }
                    />
                    <Route
                        path="/superadmin/billing"
                        element={
                            <SupperAdminRoute>
                                <SuperAdminBilling />
                            </SupperAdminRoute>
                        }
                    />
                    <Route
                        path="/superadmin/analytics"
                        element={
                            <SupperAdminRoute>
                                <SuperAdminAnalytics />
                            </SupperAdminRoute>
                        }
                    />
                    <Route
                        path="/superadmin/settings"
                        element={
                            <SupperAdminRoute>
                                <SuperAdminSettings />
                            </SupperAdminRoute>
                        }
                    />
                    <Route
                        path="/superadmin/dns-config"
                        element={
                            <SupperAdminRoute>
                                <SuperAdminDNSConfig />
                            </SupperAdminRoute>
                        }
                    />
                    <Route
                        path="/superadmin/dns-records"
                        element={
                            <SupperAdminRoute>
                                <SuperAdminDNSRecords />
                            </SupperAdminRoute>
                        }
                    />
                    <Route
                        path="/superadmin/domains"
                        element={
                            <SupperAdminRoute>
                                <SuperAdminDomains />
                            </SupperAdminRoute>
                        }
                    />
                    <Route
                        path="/superadmin/logs"
                        element={
                            <SupperAdminRoute>
                                <SuperAdminLogs />
                            </SupperAdminRoute>
                        }
                    />
                    <Route
                        path="/superadmin/sms-marketing"
                        element={
                            <SupperAdminRoute>
                                <SuperAdminSMSMarketing />
                            </SupperAdminRoute>
                        }
                    />
                    <Route
                        path="/superadmin/call-numbers"
                        element={
                            <SupperAdminRoute>
                                <SuperAdminCallNumbers />
                            </SupperAdminRoute>
                        }
                    />
                    <Route
                        path="/superadmin/support-tickets"
                        element={
                            <SupperAdminRoute>
                                <SuperAdminSupportTickets />
                            </SupperAdminRoute>
                        }
                    />
                    <Route
                        path="/superadmin/email-management"
                        element={
                            <SupperAdminRoute>
                                <SuperAdminEmailManagement />
                            </SupperAdminRoute>
                        }
                    />
                    <Route
                        path="/superadmin/email-inbox"
                        element={
                            <SupperAdminRoute>
                                <SuperAdminEmailInbox />
                            </SupperAdminRoute>
                        }
                    />

                    <Route path="/fb" element={<FB />} />
                    <Route path="/success" element={<Success />} />
                    <Route path="/fail" element={<Fail />} />
                    <Route path="/callback" element={<Callback />} />
                    <Route path="/callbackSMS" element={<CallbackSMS />} />
                </Routes>
            </BrowserRouter>
            </BusinessSettingsProvider>
        </div>
    );
};

export default App;
